import { PassengerProfile } from "../types";

/**
 * True if a tool's function name is the Copa passenger/booking lookup (the crew's
 * Refund/Routing agents call `get_booking`). Kept broad so a renamed lookup tool
 * still populates the profile — widen further if a live frame shows a new name.
 */
export function isGetBookingFn(name: string): boolean {
  return /get[_\s-]*booking|lookup[_\s-]*booking|verify[_\s-]*booking|get[_\s-]*passenger/i.test(name ?? "");
}

function str(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === "string") return v.trim() || undefined;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}

/**
 * Pull the flat booking record out of whatever envelope the tool_call output uses.
 * voiceX wraps non-object outputs as `{ value }`, and some agents return a JSON
 * string, an array, or nest under `data`/`result`/`booking`. Handle all of them.
 */
function unwrapBooking(output: unknown): Record<string, unknown> | null {
  let cur: unknown = output;
  for (let i = 0; i < 5 && cur != null; i++) {
    if (typeof cur === "string") {
      const s = cur.trim();
      if (!s) return null;
      // extract a JSON object/array even from a wrapper like  text='[{...}]'
      const m = s.match(/[[{][\s\S]*[\]}]/);
      try { cur = JSON.parse(m ? m[0] : s); continue; } catch { return null; }
    }
    if (Array.isArray(cur)) { cur = cur[0]; continue; }
    if (typeof cur === "object") {
      const o = cur as Record<string, unknown>;
      if ("value" in o && (typeof o.value === "string" || (o.value && typeof o.value === "object"))) { cur = o.value; continue; }
      if ("data" in o && o.data && typeof o.data === "object") { cur = o.data; continue; }
      if ("result" in o && o.result && typeof o.result === "object") { cur = o.result; continue; }
      if ("booking" in o && o.booking && typeof o.booking === "object") { cur = o.booking; continue; }
      return o; // looks like the flat record
    }
    return null;
  }
  return cur && typeof cur === "object" && !Array.isArray(cur) ? (cur as Record<string, unknown>) : null;
}

/** Map a get_booking tool output (snake_case) into the camelCase PassengerProfile. */
export function mapBookingToProfile(output: unknown): PassengerProfile | null {
  const r = unwrapBooking(output);
  if (!r) return null;
  const profile: PassengerProfile = {
    bookingRef:        str(r.booking_ref ?? r.bookingRef),
    passengerId:       str(r.passenger_id ?? r.passengerId),
    firstName:         str(r.first_name ?? r.firstName),
    lastName:          str(r.last_name ?? r.lastName),
    email:             str(r.email),
    preferredLanguage: str(r.preferred_language ?? r.preferredLanguage),
    loyaltyTier:       str(r.loyalty_tier ?? r.loyaltyTier),
    origin:            str(r.origin),
    destination:       str(r.destination),
    route:             str(r.route),
    travelDate:        str(r.travel_date ?? r.travelDate),
    flightNumber:      str(r.flight_number ?? r.flightNumber),
    fareType:          str(r.fare_type ?? r.fareType),
    bookingStatus:     str(r.booking_status ?? r.bookingStatus),
    ticketStatus:      str(r.ticket_status ?? r.ticketStatus),
    refundCaseId:      str(r.refund_case_id ?? r.refundCaseId),
    refundReason:      str(r.refund_reason ?? r.refundReason),
    refundStatus:      str(r.refund_status ?? r.refundStatus),
    refundStage:       str(r.refund_stage ?? r.refundStage),
    refundAmount:      str(r.refund_amount ?? r.refundAmount),
    currency:          str(r.currency),
    expectedSla:       str(r.expected_sla ?? r.expectedSla),
    voucherId:         str(r.voucher_id ?? r.voucherId),
    voucherEligible:   str(r.voucher_eligible ?? r.voucherEligible),
    agentRequired:     str(r.agent_required ?? r.agentRequired),
    specialHandling:   str(r.special_handling ?? r.specialHandling),
    policyNote:        str(r.policy_note ?? r.policyNote),
  };
  // Require at least an identifier so a no-result/[] output doesn't blank the card.
  if (!profile.bookingRef && !profile.firstName && !profile.passengerId) return null;
  return profile;
}
